import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { t, font, Button, Input, Panel } from "@realtyflows/ui";
import { submitContact } from "../api";
import Alert from "../components/Alert";
import useResponsive from "../hooks/useResponsive";
import useScrollToAlert from "../hooks/useScrollToError";

export default function Contact({ embedded, user }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const responsive = useResponsive();
  const isEnterprise = searchParams.get("plan") === "enterprise";
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    subject: isEnterprise ? "Enterprise Plan Inquiry" : "",
    message: isEnterprise ? "I'm interested in the Enterprise plan for 20+ users. Please contact me with more details." : "",
  });
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const errorRef = useScrollToAlert(error);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = "Contact Us — RealtyFlows";
    return () => { document.title = "RealtyFlows"; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await submitContact(form);
      setSent(true);
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ background: t.pageBg }}>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "16px 40px", background: t.surface, borderBottom: `1px solid ${t.border}`,
        position: "sticky", top: 0, zIndex: 10,
      }}>
        <div onClick={() => navigate(embedded ? "/dashboard" : "/")} style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
          <img src="/logo.png" alt="RealtyFlows" style={{ width: 28, height: 28, borderRadius: 4 }} />
          <div style={{ fontSize: 18, fontWeight: 600, color: t.textPrimary, fontFamily: font.display }}>
            Realty<span style={{ color: t.gold }}>Flows</span>
          </div>
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          {embedded ? (
            <span onClick={() => navigate(-1)} style={{ fontSize: 14, color: t.emerald, cursor: "pointer", fontWeight: 500, fontFamily: font.body }}>&larr; Go Back</span>
          ) : (
            <>
              <span onClick={() => navigate("/login")} style={{ fontSize: 14, color: t.textMuted, cursor: "pointer", fontWeight: 500, fontFamily: font.body }}>Login</span>
              <Button variant="primary" onClick={() => navigate("/signup")}>Get Started Free</Button>
            </>
          )}
        </div>
      </div>

      <div style={{ padding: "60px 20px" }}>
      <div style={{ maxWidth: 720, margin: "0 auto" }}>

        <h1 style={{ fontSize: 28, fontWeight: 500, color: t.textPrimary, fontFamily: font.display, marginBottom: 8 }}>Contact Us</h1>
        <p style={{ fontSize: 14, color: t.textSecondary, fontFamily: font.body, marginBottom: 32 }}>Have a question or need help? We'd love to hear from you.</p>

        <div style={{ display: "grid", gridTemplateColumns: responsive.settingsColumns, gap: 24 }}>
          {/* Contact Form */}
          <Panel padding={24}>
            {sent ? (
              <div style={{ textAlign: "center", padding: "40px 0" }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>&#10003;</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: t.textPrimary, fontFamily: font.body, marginBottom: 8 }}>Message Sent!</div>
                <div style={{ fontSize: 13, color: t.textSecondary, fontFamily: font.body, marginBottom: 20 }}>We'll get back to you within 24 hours.</div>
                <Button variant="outline" size="sm" onClick={() => setSent(false)}>Send Another</Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div style={{ fontSize: 16, fontWeight: 700, color: t.textPrimary, fontFamily: font.body, marginBottom: 20 }}>Send a Message</div>
                <Alert ref={errorRef} message={error} onDismiss={() => setError("")} style={{ marginBottom: 16 }} />
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                  {user ? (
                    <>
                      <div>
                        <div style={{ fontSize: 11, color: t.textSecondary, marginBottom: 4, fontWeight: 600, fontFamily: font.body }}>Name</div>
                        <div style={{
                          background: t.border, border: `1.5px solid ${t.border}`, borderRadius: 8,
                          padding: "10px 12px", color: t.textPrimary, fontSize: 13, fontFamily: font.body,
                          opacity: 0.8, cursor: "not-allowed",
                        }}>{form.name}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: t.textSecondary, marginBottom: 4, fontWeight: 600, fontFamily: font.body }}>Email</div>
                        <div style={{
                          background: t.border, border: `1.5px solid ${t.border}`, borderRadius: 8,
                          padding: "10px 12px", color: t.textPrimary, fontSize: 13, fontFamily: font.body,
                          opacity: 0.8, cursor: "not-allowed",
                        }}>{form.email}</div>
                      </div>
                    </>
                  ) : (
                    <>
                      <Input label="Name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Your name" required />
                      <Input label="Email" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="you@email.com" required />
                    </>
                  )}
                </div>
                <div style={{ marginBottom: 12 }}>
                  <Input label="Subject" value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} placeholder="How can we help?" required />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, color: t.textSecondary, marginBottom: 4, fontWeight: 600, fontFamily: font.body }}>Message</div>
                  <textarea value={form.message} onChange={e => setForm({ ...form, message: e.target.value })}
                    placeholder="Tell us more..."
                    required
                    style={{
                      width: "100%", minHeight: 120, boxSizing: "border-box",
                      background: t.surfaceWarm, border: `1.5px solid ${t.border}`, borderRadius: 8,
                      padding: 12, color: t.textPrimary, fontSize: 13, fontFamily: font.body,
                      outline: "none", resize: "vertical", lineHeight: 1.6,
                    }} />
                </div>
                <Button variant="primary" fullWidth type="submit" disabled={loading}>
                  {loading ? "Sending..." : "Send Message"}
                </Button>
              </form>
            )}
          </Panel>

          {/* Contact Info */}
          <div>
            {[
              { icon: "✉", title: "Email", detail: "support@realtyflows.com", sub: "We respond within 24 hours" },
              { icon: "✆", title: "Phone", detail: "(312) 555-0200", sub: "Mon-Fri 9am-6pm CT" },
              { icon: "◎", title: "Office", detail: "123 N Michigan Ave, Suite 1000", sub: "Chicago, IL 60601" },
            ].map(c => (
              <Panel key={c.title} padding={20} style={{ marginBottom: 16 }}>
                <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                  <div style={{ width: 40, height: 40, borderRadius: 10, background: t.emeraldLight, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>{c.icon}</div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: t.textPrimary, fontFamily: font.body }}>{c.title}</div>
                    <div style={{ fontSize: 14, color: t.emerald, fontWeight: 600, fontFamily: font.body, marginTop: 2 }}>{c.detail}</div>
                    <div style={{ fontSize: 11, color: t.textMuted, fontFamily: font.body, marginTop: 2 }}>{c.sub}</div>
                  </div>
                </div>
              </Panel>
            ))}

            <Panel padding={20}>
              <div style={{ fontSize: 13, fontWeight: 700, color: t.textPrimary, fontFamily: font.body, marginBottom: 8 }}>Quick Links</div>
              {[
                { label: "Terms of Service", path: "/terms" },
                { label: "Privacy Policy", path: "/privacy" },
              ].map(l => (
                <div key={l.path} onClick={() => navigate(l.path)}
                  style={{ fontSize: 13, color: t.emerald, cursor: "pointer", padding: "6px 0", fontFamily: font.body }}>
                  {l.label} &rarr;
                </div>
              ))}
            </Panel>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
